import { FormEvent, useState, useEffect, useRef, forwardRef } from "react";
import { useSearchContext } from "../contexts/SearchContext";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate } from "react-router-dom";
import { FaUser, FaMapMarkerAlt, FaSearch, FaCalendarAlt } from 'react-icons/fa';
import NightsDropdown from "./Common/NightsDropDown";
import VacationTypeDropdown from "./Common/VacationTypeDropDown";
// import CustomDatePicker from "./Common/DatePicker";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "";

const CustomDateButton = forwardRef<HTMLButtonElement, { value: string; onClick: () => void }>(
  ({ value, onClick }, ref) => (
    <div className="relative">
      <button
        ref={ref}
        type="button"
        onClick={onClick}
        className="w-[230px] text-left bg-white px-4 py-2 ml-2 flex items-center justify-between focus:outline-none text-sm"
      >
        {value || "Select Dates"}
      </button>
    </div>
  )
);

interface SearchSummaryBarProps {
  country: string;  // Assuming type is of type string
}

const SearchBar = ({ country }: SearchSummaryBarProps) => {
  const navigate = useNavigate();
  const search = useSearchContext();

  const [destination, setDestination] = useState<string>(country || search.destination);
  // const [suggestions, setSuggestions] = useState<string[]>([]);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const [checkIn, setCheckIn] = useState<Date>(search.checkIn);
  const [checkOut, setCheckOut] = useState<Date>(search.checkOut);
  const [adultCount, setAdultCount] = useState<number>(search.adultCount);
  const [childCount, setChildCount] = useState<number>(search.childCount);
  const [childAges, setChildAges] = useState<number[]>(new Array(search.childCount).fill(0));
  const [roomCount, setRoomCount] = useState<number>(1); // State for rooms
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [destinationsData, setDestinationsData] = useState<any[]>([]);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const suggestionsRef = useRef<HTMLUListElement | null>(null);
  const modalRef = useRef<HTMLDivElement | null>(null);
  const [nights, setNights] = useState<number>(3);
  const [monthsShown, setMonthsShown] = useState(2);
  const [vacationType, setVacationType] = useState('STD');
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState<string[]>([]);

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    search.saveSearchValues(
      destination,
      checkIn,
      checkOut,
      adultCount,
      childCount,
      childAges,
      roomCount,
      nights,
      vacationType,
    );
    const currentPath = window.location.pathname;

    if (currentPath.includes("/resort")) {
      navigate(currentPath);
      window.location.reload();
    } else {
      navigate("/search");
    }
  };

  const handleResize = () => {
    if (window.innerWidth <= 768) {
      setMonthsShown(1);
    } else {
      setMonthsShown(2);
    }
  };

  useEffect(() => {
    handleResize();

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const addDays = (date: Date, days: number) => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  };

  const calculateNights = (checkInDate: Date, checkOutDate: Date) => {
    const timeDiff = checkOutDate.getTime() - checkInDate.getTime();
    return Math.ceil(timeDiff / (1000 * 3600 * 24));
  };

  const handleClearAll = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    const currentDate = new Date();
    const defaultCheckInDate = addDays(currentDate, 5);
    const defaultCheckOutDate = addDays(defaultCheckInDate, 3);

    setAdultCount(2);
    setChildCount(0);
    setChildAges([]);
    setRoomCount(1);
    setDestination("");
    setCheckIn(defaultCheckInDate);
    setCheckOut(defaultCheckOutDate);
    // setSuggestions([]);
    setHighlightedIndex(-1);
    setNights(3);
    setVacationType("STD");
  };


  const minDate = new Date();
  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() + 1);

  useEffect(() => {
    setChildAges(search.childAges);
    setRoomCount(search.roomCount);
    setNights(search.nights);
    setVacationType(search.vacationType);
  }, [search.childAges, search.roomCount, search.nights]);

  useEffect(() => {
    const fetchDestinations = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/hotels`);
        const data = await response.json();
        setDestinationsData(data);
      } catch (error) {
        console.error("Error fetching destinations:", error);
      }
    };

    fetchDestinations();
  }, []);

  const handleDestinationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setDestination(value);

    if (value.length > 0) {
      const newSuggestions = destinationsData
        .map(dest => dest.country)
        .filter((country, index, self) =>
          country.toLowerCase().includes(value.toLowerCase()) && self.indexOf(country) === index
        );
      setFilteredSuggestions(newSuggestions);
    } else {
      // Show all unique countries if the input is empty
      const uniqueCountries = Array.from(new Set(destinationsData.map(dest => dest.country)));
      setFilteredSuggestions(uniqueCountries);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setDestination(suggestion);
    setFilteredSuggestions([]);
    setIsFocused(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (filteredSuggestions.length > 0) {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setHighlightedIndex((prevIndex) =>
          prevIndex < filteredSuggestions.length - 1 ? prevIndex + 1 : 0
        );
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        setHighlightedIndex((prevIndex) =>
          prevIndex > 0 ? prevIndex - 1 : filteredSuggestions.length - 1
        );
      } else if (event.key === "Enter" && highlightedIndex >= 0) {
        event.preventDefault();
        handleSuggestionClick(filteredSuggestions[highlightedIndex]);
      }
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      inputRef.current && !inputRef.current.contains(event.target as Node) &&
      suggestionsRef.current && !suggestionsRef.current.contains(event.target as Node)
    ) {
      setIsFocused(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleDateChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setCheckIn(start!);
    setCheckOut(end!);

    if (start && end) {
      handleCheckOutChange(end);
    }
  };

  const handleCheckOutChange = (date: Date) => {
    setCheckOut(date);

    const updatedNights = calculateNights(checkIn, date);
    setNights(updatedNights);
  };

  const handleNightsChange = (selectedNights: number) => {
    setNights(selectedNights);
    const checkOutDate = addDays(checkIn, selectedNights);
    setCheckOut(checkOutDate);
  };

  const handleFocus = () => {
    setIsFocused(true);
    const uniqueCountries = Array.from(new Set(destinationsData.map(dest => dest.country)));
    setFilteredSuggestions(uniqueCountries);
  };

  const handleVacationTypeChange = (selectedVacationType: string) => {
    setVacationType(selectedVacationType);
  }

  const sortedSuggestions = [...filteredSuggestions].sort((a, b) =>
    a.localeCompare(b)
  );

  return (
    <div className="relative mt-32 md:mt-32 z-10">
      <form
        onSubmit={handleSubmit}
        className="md:mx-3 p-3 border border-gray-400 md:border-gray-300 rounded-md md:rounded-3xl xl:rounded-full shadow bg-white items-center"
      >
        <div className="flex flex-col md:flex-row w-full md:space-x-4">
          <div className="flex flex-col flex-1 border-b md:border-b-0 md:border-r border-gray-400 px-2 relative">
            <label className="font-bold text-gray-600 mb-1 md:ml-4">Destination</label>
            <div className="w-full flex items-center">
              <FaMapMarkerAlt className="text-halo-pink md:ml-3" />
              <input
                ref={inputRef}
                placeholder="Going to?"
                className="w-full text-left bg-white px-4 py-2 focus:outline-none"
                value={destination}
                onFocus={handleFocus}
                onChange={handleDestinationChange}
                onKeyDown={handleKeyDown}
                required
              />
            </div>
            <ul
              ref={suggestionsRef}
              style={{ width: window.innerWidth > 1500 ? "90%" : "250px" }}
              className="absolute z-10 bg-white rounded-md shadow-lg max-h-60 overflow-y-auto mt-16 scrollbar-initial"
            >
              {isFocused && sortedSuggestions.map((suggestion, index) => (
                <li
                  key={index}
                  className={`flex items-center space-x-2 px-4 py-2 cursor-pointer hover:bg-halo-pink-bg hover:text-halo-pink border-b border-gray-100 ${index === highlightedIndex ? "bg-halo-pink-bg text-halo-pink" : ""
                    }`}
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  <span className="text-lg text-gray-500">
                    <img width="20" height="20" src="https://img.icons8.com/ios-glyphs/30/737373/earth-planet--v2.png" alt="earth-planet--v2" />
                  </span>
                  <span>{suggestion}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex flex-col flex-1 border-b md:border-b-0 md:border-r border-gray-400 px-2 relative">
            <label className="font-bold text-gray-600 mb-1 mt-2 md:mt-0">Check-in & Check-out</label>
            <div className="w-full relative">
              <div className="w-full flex items-center text-[14px]">
                <DatePicker
                  id="date-range-picker"
                  selected={checkIn}
                  onChange={handleDateChange}
                  startDate={checkIn}
                  endDate={checkOut}
                  minDate={minDate}
                  selectsRange
                  monthsShown={monthsShown}
                  dateFormat="dd/MM/yyyy"
                  className="w-[225px] text-left bg-white px-4 py-2 ml-2 focus:outline-none"
                  calendarClassName="custom-datepicker"
                  showPopperArrow={true}
                  customInput={
                    <CustomDateButton
                      value={`${checkIn?.toLocaleDateString() || "Check-in"} - ${checkOut?.toLocaleDateString() || "Check-out"}`} onClick={function (): void {
                        throw new Error("Function not implemented.");
                      }} />
                  }
                />
                <FaCalendarAlt className="absolute top-2.5 text-halo-pink" />
              </div>
            </div>
          </div>

          <div className="flex flex-col border-b md:border-b-0 lg:border-r border-gray-400 px-4 relative">
            <label className="font-bold text-gray-600 mb-2 mt-2 md:mt-0">Nights</label>
            <div className="w-full relative">
              <NightsDropdown nights={nights} handleNightsChange={handleNightsChange} />
            </div>
          </div>


          <div className="md:hidden lg:block xl:block 2xl:block">
            <div className="flex flex-col border-b md:border-b-0 xl:border-r border-gray-400 px-4 relative" style={{ minHeight: '68px' }}>
              <label className="font-bold text-gray-600 mb-2 md:ml-4 mt-2 md:mt-0">Type</label>
              <div className="w-full relative">
                <VacationTypeDropdown selectedVacationType={vacationType} handleVacationTypeChange={handleVacationTypeChange} />
              </div>
            </div>
          </div>

          <div className="md:hidden lg:hidden xl:block 2xl:block">
            <div className="flex flex-col px-4 relative" style={{ minHeight: '68px' }}>
              <label className="font-bold text-gray-600 mb-1 md:ml-4 mt-2 md:mt-0">Guests</label>
              <div className="relative w-full">
                <div
                  className="flex items-center rounded bg-white md:px-4 py-2 cursor-pointer"
                  onClick={toggleModal}
                >
                  <FaUser className="text-halo-pink mr-3" />
                  <span className="guest-count">
                    {adultCount} <span className="text-sm">{adultCount === 1 ? "Adult" : "Adults"},</span> {childCount} <span className="text-sm">{childCount === 1 ? "Child" : "Children"}</span>
                  </span>
                </div>
                {isModalOpen && (
                  <div ref={modalRef} className="absolute z-20 bg-white border border-gray-300 p-6 mt-2 shadow-lg w-[320px] rounded-lg">
                    <button className="absolute top-2 right-2 text-gray-500 hover:text-gray-800" onClick={toggleModal}>&#x2715;</button>

                    <div className="flex flex-col gap-6">
                      {/* Adults Section */}
                      <div className="flex justify-between items-center">
                        <label className="font-medium text-gray-700">Adults</label>
                        <div className="flex items-center space-x-3">
                          <button
                            type="button"
                            className="w-8 h-8 bg-gray-200 text-gray-700 rounded-full flex items-center justify-center"
                            onClick={() => setAdultCount(adultCount > 1 ? adultCount - 1 : 1)}
                          >
                            -
                          </button>
                          <span className="text-gray-800">{adultCount}</span>
                          <button
                            type="button"
                            className="w-8 h-8 bg-gray-200 text-gray-700 rounded-full flex items-center justify-center"
                            onClick={() => setAdultCount(adultCount + 1)}
                          >
                            +
                          </button>
                        </div>
                      </div>

                      {/* Children Section */}
                      <div className="flex justify-between items-center">
                        <label className="font-medium text-gray-700">Children</label>
                        <div className="flex items-center space-x-3">
                          <button
                            type="button"
                            className="w-8 h-8 bg-gray-200 text-gray-700 rounded-full flex items-center justify-center"
                            onClick={() => {
                              if (childCount > 0) {
                                const newChildCount = childCount - 1;
                                const newChildAges = childAges.slice(0, newChildCount);

                                setChildCount(newChildCount);
                                setChildAges(newChildAges);

                                // Save the updated child count and child ages in the context
                                search.saveSearchValues(
                                  search.destination,
                                  search.checkIn,
                                  search.checkOut,
                                  search.adultCount,
                                  newChildCount,
                                  newChildAges,
                                  roomCount,
                                  nights,
                                  vacationType,
                                );
                              }
                            }}
                          >
                            -
                          </button>
                          <span className="text-gray-800">{childCount}</span>
                          <button
                            type="button"
                            className="w-8 h-8 bg-gray-200 text-gray-700 rounded-full flex items-center justify-center"
                            onClick={() => {
                              const newChildCount = childCount + 1;
                              const newChildAges = [...childAges, 0];

                              setChildCount(newChildCount);
                              setChildAges(newChildAges);

                              // Save the updated child count and child ages in the context
                              search.saveSearchValues(
                                search.destination,
                                search.checkIn,
                                search.checkOut,
                                search.adultCount,
                                newChildCount,
                                newChildAges,
                                roomCount,
                                nights,
                                vacationType,
                              );
                            }}
                          >
                            +
                          </button>
                        </div>
                      </div>


                      {/* Child Age Selectors */}
                      {Array.from({ length: childCount }).map((_, index) => (
                        <div key={index} className="flex flex-col gap-1">
                          <label className="font-medium text-gray-700">Child {index + 1} Age</label>
                          <select
                            className={`w-full p-2 border rounded-md focus:outline-none ${childAges[index] === 0 ? 'border-red-600' : 'focus:ring-2 focus:ring-gray-400'}`}
                            value={childAges[index]?.toString() || ''}  // Ensure the value is a string for the dropdown
                            onChange={(event) => {
                              const newAges = [...childAges];
                              newAges[index] = parseInt(event.target.value, 10);  // Convert string back to number
                              setChildAges(newAges);  // Update the state
                              // Save the updated child ages in the search context
                              search.saveSearchValues(
                                destination,
                                checkIn,
                                checkOut,
                                adultCount,
                                childCount,
                                newAges,  // Update childAges here
                                roomCount,
                                nights,
                                vacationType,
                              );
                            }}
                          >
                            <option value="" disabled>{childAges[index] ? `${childAges[index]} years old` : 'Age needed'}</option>
                            {Array.from({ length: 18 }, (_, i) => (
                              <option key={i} value={i + 1}>{i + 1} years old</option>
                            ))}
                          </select>
                        </div>
                      ))}

                      {/* Rooms Section */}
                      {/* <div className="flex justify-between items-center">
                      <label className="font-medium text-gray-700">Rooms</label>
                      <div className="flex items-center space-x-3">
                        <button
                          type="button"
                          className="w-8 h-8 bg-gray-200 text-gray-700 rounded-full flex items-center justify-center"
                          onClick={() => {
                            const newRoomCount = roomCount > 1 ? roomCount - 1 : 1;
                            setRoomCount(newRoomCount);
                            search.saveSearchValues(
                              destination,
                              checkIn,
                              checkOut,
                              adultCount,
                              childCount,
                              childAges,
                              newRoomCount,  // Update room count
                              nights,
                              vacationType,
                            );
                          }}
                        >
                          -
                        </button>
                        <span className="text-gray-800">{roomCount}</span>
                        <button
                          type="button"
                          className="w-8 h-8 bg-gray-200 text-gray-700 rounded-full flex items-center justify-center"
                          onClick={() => {
                            const newRoomCount = roomCount + 1;
                            setRoomCount(newRoomCount);
                            search.saveSearchValues(
                              destination,
                              checkIn,
                              checkOut,
                              adultCount,
                              childCount,
                              childAges,
                              newRoomCount,
                              nights,
                              vacationType,
                            );
                          }}
                        >
                          +
                        </button>
                      </div>
                    </div> */}



                      {/* Done Button */}
                      <button
                        type="button"
                        className="w-full mt-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors duration-200"
                        onClick={() => {
                          search.saveSearchValues(
                            destination,
                            checkIn,
                            checkOut,
                            adultCount,
                            childCount,
                            childAges,
                            roomCount,
                            nights,
                            vacationType,
                          );
                          toggleModal(); // Close the modal after saving the values
                        }}
                      >
                        Done
                      </button>
                    </div>
                  </div>
                )}

              </div>
            </div>
          </div>

          <div className="md:hidden lg:hidden xl:block 2xl:block">
            <div className="flex items-center px-4 mt-6 md:mt-0 gap-1" style={{ minHeight: '68px' }}>
              <button
                type="button"
                onClick={handleClearAll}
                className="md:w-20 md:h-10 h-12 text-[20px] md:text-[16px] md:rounded-full rounded-md flex items-center justify-center w-60 underline text-halo-pink hover:text-pink-800 md:border"
                aria-label="Clear search"
              >
                Clear
              </button>
              <button
                type="submit"
                className="md:w-10 md:h-10 h-12 bg-halo-pink text-white md:rounded-full rounded-md flex items-center justify-center w-60 hover:bg-pink-800"
              >
                <FaSearch className="hidden md:block text-white" />
                <span className="md:hidden text-[20px]">Search</span>
              </button>
            </div>
          </div>
        </div>

        {/* Tab View */}

        <div className="hidden md:block lg:block xl:hidden 2xl:hidden">
          <hr className="my-2 border-gray-400" />
          <div className="flex justify-center">
            <div className="flex flex-col border-b md:border-b-0 md:border-r border-gray-400 px-4 relative lg:hidden">
              <label className="font-bold text-gray-600 mb-2 md:ml-4 mt-2 md:mt-0">Type</label>
              <div className="w-full relative">
                <VacationTypeDropdown selectedVacationType={vacationType} handleVacationTypeChange={handleVacationTypeChange} />
              </div>
            </div>

            <div className="flex flex-col px-4 relative border-r border-gray-400">
              <label className="font-bold text-gray-600 mb-1 md:ml-4 mt-2 md:mt-0">Guests</label>
              <div className="relative w-full">
                <div
                  className="flex items-center rounded bg-white md:px-4 py-2 cursor-pointer"
                  onClick={toggleModal}
                >
                  <FaUser className="text-halo-pink mr-3" />
                  <span className="guest-count">
                    {adultCount} <span className="text-sm">{adultCount === 1 ? "Adult" : "Adults"},</span> {childCount} <span className="text-sm">{childCount === 1 ? "Child" : "Children"}</span>
                  </span>
                </div>
                {isModalOpen && (
                  <div ref={modalRef} className="absolute z-20 bg-white border border-gray-300 p-6 mt-2 shadow-lg w-[320px] rounded-lg">
                    <button className="absolute top-2 right-2 text-gray-500 hover:text-gray-800" onClick={toggleModal}>&#x2715;</button>

                    <div className="flex flex-col gap-6">
                      {/* Adults Section */}
                      <div className="flex justify-between items-center">
                        <label className="font-medium text-gray-700">Adults</label>
                        <div className="flex items-center space-x-3">
                          <button
                            type="button"
                            className="w-8 h-8 bg-gray-200 text-gray-700 rounded-full flex items-center justify-center"
                            onClick={() => setAdultCount(adultCount > 1 ? adultCount - 1 : 1)}
                          >
                            -
                          </button>
                          <span className="text-gray-800">{adultCount}</span>
                          <button
                            type="button"
                            className="w-8 h-8 bg-gray-200 text-gray-700 rounded-full flex items-center justify-center"
                            onClick={() => setAdultCount(adultCount + 1)}
                          >
                            +
                          </button>
                        </div>
                      </div>

                      {/* Children Section */}
                      <div className="flex justify-between items-center">
                        <label className="font-medium text-gray-700">Children</label>
                        <div className="flex items-center space-x-3">
                          <button
                            type="button"
                            className="w-8 h-8 bg-gray-200 text-gray-700 rounded-full flex items-center justify-center"
                            onClick={() => {
                              if (childCount > 0) {
                                const newChildCount = childCount - 1;
                                const newChildAges = childAges.slice(0, newChildCount);

                                setChildCount(newChildCount);
                                setChildAges(newChildAges);

                                // Save the updated child count and child ages in the context
                                search.saveSearchValues(
                                  search.destination,
                                  search.checkIn,
                                  search.checkOut,
                                  search.adultCount,
                                  newChildCount,
                                  newChildAges,
                                  roomCount,
                                  nights,
                                  vacationType,
                                );
                              }
                            }}
                          >
                            -
                          </button>
                          <span className="text-gray-800">{childCount}</span>
                          <button
                            type="button"
                            className="w-8 h-8 bg-gray-200 text-gray-700 rounded-full flex items-center justify-center"
                            onClick={() => {
                              const newChildCount = childCount + 1;
                              const newChildAges = [...childAges, 0];

                              setChildCount(newChildCount);
                              setChildAges(newChildAges);

                              // Save the updated child count and child ages in the context
                              search.saveSearchValues(
                                search.destination,
                                search.checkIn,
                                search.checkOut,
                                search.adultCount,
                                newChildCount,
                                newChildAges,
                                roomCount,
                                nights,
                                vacationType,
                              );
                            }}
                          >
                            +
                          </button>
                        </div>
                      </div>


                      {/* Child Age Selectors */}
                      {Array.from({ length: childCount }).map((_, index) => (
                        <div key={index} className="flex flex-col gap-1">
                          <label className="font-medium text-gray-700">Child {index + 1} Age</label>
                          <select
                            className={`w-full p-2 border rounded-md focus:outline-none ${childAges[index] === 0 ? 'border-red-600' : 'focus:ring-2 focus:ring-gray-400'}`}
                            value={childAges[index]?.toString() || ''}  // Ensure the value is a string for the dropdown
                            onChange={(event) => {
                              const newAges = [...childAges];
                              newAges[index] = parseInt(event.target.value, 10);  // Convert string back to number
                              setChildAges(newAges);  // Update the state
                              // Save the updated child ages in the search context
                              search.saveSearchValues(
                                destination,
                                checkIn,
                                checkOut,
                                adultCount,
                                childCount,
                                newAges,  // Update childAges here
                                roomCount,
                                nights,
                                vacationType,
                              );
                            }}
                          >
                            <option value="" disabled>{childAges[index] ? `${childAges[index]} years old` : 'Age needed'}</option>
                            {Array.from({ length: 18 }, (_, i) => (
                              <option key={i} value={i + 1}>{i + 1} years old</option>
                            ))}
                          </select>
                        </div>
                      ))}

                      {/* Done Button */}
                      <button
                        type="button"
                        className="w-full mt-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors duration-200"
                        onClick={() => {
                          search.saveSearchValues(
                            destination,
                            checkIn,
                            checkOut,
                            adultCount,
                            childCount,
                            childAges,
                            roomCount,
                            nights,
                            vacationType,
                          );
                          toggleModal(); // Close the modal after saving the values
                        }}
                      >
                        Done
                      </button>
                    </div>
                  </div>
                )}

              </div>
            </div>
            <div className="flex flex-row items-center mt-4 ml-10">
              <div className="flex items-center px-4 mt-6 md:mt-0 gap-1">
                <button
                  type="button"
                  onClick={handleClearAll}
                  className="md:w-20 md:h-10 h-12 text-[20px] md:text-[16px] md:rounded-full rounded-md flex items-center justify-center w-60 underline text-halo-pink hover:text-pink-800 md:border"
                  aria-label="Clear search"
                >
                  Clear
                </button>
                <button
                  type="submit"
                  className="md:w-10 md:h-10 h-12 bg-halo-pink text-white md:rounded-full rounded-md flex items-center justify-center w-60 hover:bg-pink-800"
                >
                  <FaSearch className="hidden md:block text-white" />
                  <span className="md:hidden text-[20px]">Search</span>
                </button>
              </div>
            </div>
          </div>

        </div>

      </form >
    </div >
  );
};

export default SearchBar;

